/**
 * frontend/src/components/OccurrenceCalendar.jsx
 *
 * The history of a repeating activity: which days it fell on, and what
 * became of each.
 *
 * It reads GET /activities/:id/occurrences, which works out the days from
 * the same recurrence rule the Workplan and the To-Do list use, and writes
 * through PUT /workplan/occurrence, which already existed. Nothing here
 * decides when an activity repeats or what "done" means; it only shows it.
 *
 * Deliberately compact: it sits under an activity's details, not instead
 * of them.
 */
import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { activitiesAPI, workplanAPI } from '../utils/api';
import { useToast, apiError } from './Toast';

const SAPPHIRE = '#2E539F';
const JAVA     = '#5CC3BC';
const AMBER    = '#b45309';   // Skipped, matching utils/workStatus.js
const CRIMSON  = '#C8372D';

/* One place deciding how each state looks and reads. */
const STATE = {
  done:     { label:'Done',     mark:'●', fg:'#1f7a74', bg:`${JAVA}22`,     bd:JAVA    },
  skipped:  { label:'Skipped',  mark:'–', fg:AMBER,     bg:`${AMBER}14`,    bd:AMBER   },
  missed:   { label:'Missed',   mark:'✕', fg:CRIMSON,   bg:`${CRIMSON}14`,  bd:CRIMSON },
  pending:  { label:'Pending',  mark:'○', fg:'#7F8C8D', bg:'var(--surface)', bd:'var(--border)' },
  upcoming: { label:'Upcoming', mark:'○', fg:'#9aa5ab', bg:'var(--surface)', bd:'var(--border)' },
};

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const DOW = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

/* Built from parts, never toISOString(): converting a local midnight to UTC
   moves the date backwards in any positive offset, which silently shifts the
   whole month by one day. */
const pad = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

/** Clicking cycles through the three states a person can set. */
const nextState = (s) => (s === 'done' ? 'skipped' : s === 'skipped' ? null : 'done');

export default function OccurrenceCalendar({ activityId, canEdit = false }) {
  const toast = useToast();
  const now = new Date();

  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);   // 1-12
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState('');               // the date being written

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const last = new Date(year, month, 0).getDate();
      const r = await activitiesAPI.getOccurrences(activityId, {
        from: ymd(year, month, 1), to: ymd(year, month, last),
      });
      setData(r.data);
    } catch (e) {
      console.error('occurrence history failed:', e);       // background load
      setData(null);
    } finally { setLoading(false); }
  }, [activityId, year, month]);

  useEffect(() => { load(); }, [load]);

  const step = (by) => {
    const m = month + by;
    if (m < 1)       { setMonth(12); setYear(y => y - 1); }
    else if (m > 12) { setMonth(1);  setYear(y => y + 1); }
    else             { setMonth(m); }
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); };

  const cycle = async (date, state) => {
    if (!canEdit || busy) return;
    setBusy(date);
    try {
      await workplanAPI.setOccurrence({
        activity_id: activityId, occurrence_date: date, status: nextState(state),
      });
      await load();
    } catch (e) {
      toast.error(apiError(e, 'Could not record that day'));
    } finally { setBusy(''); }
  };

  /* Derived values, all declared after the hooks and read only below. */
  const byDate = Object.fromEntries((data?.days || []).map(d => [d.date, d.state]));
  const s      = data?.summary;
  const todayStr = ymd(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const daysInMonth = new Date(year, month, 0).getDate();
  // Monday-first: JS getDay() has Sunday at 0, so Sunday becomes 6.
  const lead = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const cells = [...Array(lead).fill(null),
                 ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  if (data && data.repeats === false) return null;          // not a repeating activity

  /* Fixed cell size rather than aspect-square: across seven columns of a
     full-width card the squares grew to the size of a real calendar, and
     this sits underneath an activity's details rather than replacing them.
     The grid is a fixed block; the figures sit beside it instead of leaving
     the rest of the card empty. */
  const CELL = 34;
  const grid = { display:'grid', gridTemplateColumns:`repeat(7, ${CELL}px)`, gap:3 };
  const cellBase = {
    width:CELL, height:CELL, borderRadius:7,
    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-semibold text-sm flex items-center gap-1.5" style={{ color:'var(--text)' }}>
            <CalendarDays size={15} style={{ color:SAPPHIRE }} /> Occurrence History
          </h2>
          <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>
            {canEdit ? 'Click a day: pending → done → skipped'
                     : 'Read only: this is not your activity'}
          </p>
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => step(-1)} className="p-1 rounded hover:bg-[var(--surface-alt)]"
            style={{ color:'var(--text-secondary)' }} title="Previous month"><ChevronLeft size={15} /></button>
          <span className="text-xs font-medium px-1.5 whitespace-nowrap" style={{ color:'var(--text)' }}>
            {MONTHS[month - 1].slice(0, 3)} {year}
          </span>
          <button onClick={() => step(1)} className="p-1 rounded hover:bg-[var(--surface-alt)]"
            style={{ color:'var(--text-secondary)' }} title="Next month"><ChevronRight size={15} /></button>
          <button onClick={goToday}
            className="text-xs px-2 py-0.5 rounded border ml-1 hover:bg-[var(--surface-alt)]"
            style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>Today</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 rounded-full animate-spin"
            style={{ borderColor:`${SAPPHIRE}33`, borderTopColor:SAPPHIRE }} />
        </div>
      ) : (
        <div className="flex flex-wrap gap-x-6 gap-y-4">

          {/* The month */}
          <div style={{ width:'fit-content' }}>
            <div style={grid}>
              {DOW.map(d => (
                <div key={d} className="text-center font-semibold pb-0.5"
                  style={{ color:'var(--text-secondary)', fontSize:10 }}>{d[0]}</div>
              ))}
              {cells.map((day, i) => {
                if (day === null) return <div key={`b${i}`} style={{ width:CELL, height:CELL }} />;
                const date  = ymd(year, month, day);
                const state = byDate[date];
                const isToday = date === todayStr;
                if (!state) {
                  return (
                    <div key={date} style={{ ...cellBase, color:'var(--border)', fontSize:10 }}
                      title="Does not repeat on this day">{day}</div>
                  );
                }
                const st = STATE[state];
                const clickable = canEdit && state !== 'upcoming';
                return (
                  <button key={date} disabled={!clickable || busy === date}
                    onClick={() => cycle(date, state)}
                    title={`${date} · ${st.label}${clickable ? ' · click to change' : state === 'upcoming' ? ' · not yet' : ''}`}
                    style={{
                      ...cellBase,
                      background: st.bg,
                      border: isToday ? `2px solid ${SAPPHIRE}` : `1px solid ${st.bd}`,
                      cursor: clickable ? 'pointer' : 'default',
                      opacity: busy === date ? 0.5 : 1,
                    }}>
                    <span style={{ color:'var(--text-secondary)', fontSize:10, lineHeight:1 }}>{day}</span>
                    <span style={{ color:st.fg, fontSize:9, lineHeight:1.3 }}>{st.mark}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* The figures, beside the month rather than above it. Totals span
              the activity's whole life so they do not jump when paging. */}
          <div className="flex-1" style={{ minWidth:150 }}>
            {s && (
              <div className="space-y-0.5 text-xs">
                {[['Total', s.total, 'var(--text)'],
                  ['Done', s.done, STATE.done.fg],
                  ['Missed', s.missed, CRIMSON],
                  ['Skipped', s.skipped, AMBER],
                  ['Upcoming', s.upcoming, 'var(--text-secondary)'],
                  ['Rate', s.rate === null ? '—' : `${s.rate}%`,
                   s.rate === null ? 'var(--text-secondary)' : s.rate >= 50 ? STATE.done.fg : CRIMSON],
                ].map(([k, v, c]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3"
                    style={{ maxWidth:170 }}>
                    <span style={{ color:'var(--text-secondary)' }}>{k}</span>
                    <strong style={{ color:c }}>{v}</strong>
                  </div>
                ))}
              </div>
            )}

            {data?.horizon_assumed && (
              <p className="text-xs mt-2" style={{ color:'var(--text-secondary)', maxWidth:230 }}>
                No end date, so totals run to {data.horizon} and are an estimate.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-3 pt-2 border-t"
              style={{ borderColor:'var(--border)', color:'var(--text-secondary)', fontSize:10, maxWidth:230 }}>
              {Object.entries(STATE).filter(([k]) => k !== 'upcoming').map(([k, v]) => (
                <span key={k} className="flex items-center gap-1">
                  <span style={{ width:11, height:11, borderRadius:3, background:v.bg,
                                 border:`1px solid ${v.bd}`, color:v.fg, fontSize:8,
                                 display:'flex', alignItems:'center', justifyContent:'center' }}>{v.mark}</span>
                  {v.label}
                </span>
              ))}
              <span className="flex items-center gap-1">
                <span style={{ width:11, height:11, borderRadius:3, border:`2px solid ${SAPPHIRE}` }} />
                Today
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
